import { GraphQLRequest } from '../request/api_request'
import { type SetUserProfileInput, type IUser, type IUserProfile, UserStripeAccount } from '../types/user'
import { NotYetImplemented } from '../utils'
import { type UpdateUserInput, type UserProfile as GQLUserProfile, ModelSortDirection, Booking as GQLBooking, GetStripeOnboardingLinkInput } from '../graphql/types'
import StorageObject, { StorageFolder } from '../utils/storage_object'
import { IBooking } from '../types/booking'
import { IPaginatedList, IPagination } from '../types/common'
import { GET_BOOKINGS_BY_HOST, GET_BOOKINGS_BY_REQUESTER, GET_USER_PROFILE_BY_USER_ID_QUERY, GET_USER_STRIPE_ACCOUNT_QUERY, GET_USER_STRIPE_LOGIN_LINK_QUERY, GET_USER_STRIPE_ONBOARDED_LINK_QUERY, SET_USER_SPOTIFY_PLAYLIST_ID, UPDATE_USER_PROFILE_MUTATION } from './queries'
import Booking from '../studio_service/booking'

export default class UserService {
  getUserByUserId: (id: string) => Promise<IUser> = NotYetImplemented

  getUserProfileByUserId = async (id: string): Promise<IUserProfile | undefined> => {
    const response = await GraphQLRequest({
      query: GET_USER_PROFILE_BY_USER_ID_QUERY,
      variables: {
        id
      },
      publicQuery: true
    })
    if (!response.data.getUser?.profile) {
      return undefined
    }
    const profileGQL = response.data.getUser.profile as GQLUserProfile
    const profile: IUserProfile = {
      firstName: profileGQL.firstName ?? undefined,
      lastName: profileGQL.lastName ?? undefined,
      website: profileGQL.websiteLink ?? undefined,
      bio: profileGQL.bio ?? undefined
    }
    if (profileGQL.profilePicture) {
      profile.profilePicture = new StorageObject(profileGQL.profilePicture)
    }
    return profile
  }

  setUserProfile = async (userId: string, profile: SetUserProfileInput): Promise<IUserProfile> => {
    try {
      await StorageObject.deleteFileFromFolder(StorageFolder.USER_PROFILE_PICTURE, userId)
    } catch (e) {
      console.log("Couldn't delete file from folder")
      console.log(e)
      throw e
    }
    let s3Object: StorageObject | undefined
    if (profile.profilePicture) {
      s3Object = await StorageObject.saveToStorage(profile.profilePicture, StorageFolder.USER_PROFILE_PICTURE, userId)
    }
    const input: UpdateUserInput = {
      id: userId,
      profile: {
        firstName: profile.firstName,
        lastName: profile.lastName,
        websiteLink: profile.website,
        bio: profile.bio,
        profilePicture: s3Object?.s3Object
      }
    }
    const response = await GraphQLRequest({
      query: UPDATE_USER_PROFILE_MUTATION,
      variables: {
        input
      }
    })
    const profileGQL = response.data.updateUser.profile as GQLUserProfile
    return {
      firstName: profileGQL.firstName,
      lastName: profileGQL.lastName,
      profilePicture: profileGQL.profilePicture ? new StorageObject(profileGQL.profilePicture) : undefined
    }
  }

  setUserProfileSpotifyPlaylistId = async (userId: string, playlistId: string): Promise<void> => {
    const input = {
      id: userId,
      spotifyPlaylistId: playlistId
    }
    await GraphQLRequest({
      query: SET_USER_SPOTIFY_PLAYLIST_ID,
      variables: {
        input
      }
    })
  }

  getUserProfileSpotifyPlaylistId = async (userId: string): Promise<string> => {
    const response = await GraphQLRequest({
      query: GET_USER_PROFILE_BY_USER_ID_QUERY,
      variables: {
        id: userId
      },
      publicQuery: true
    })
    return response.data.getUser.spotifyPlaylistId
  }

  getRequestedBookingByUserId = async (userId: string, pagination?: IPagination | undefined): Promise<IPaginatedList<IBooking>> => {
    const response = await GraphQLRequest({
      query: GET_BOOKINGS_BY_REQUESTER,
      variables: {
        requesterUserId: userId,
        limit: pagination?.limit ?? 10,
        nextToken: pagination?.nextToken,
        sortDirection: ModelSortDirection.DESC
      },
      publicQuery: true
    })
    // TODO: Make this query more cost effective by fetching users and studios in graphql
    const gqlBookings = response.data.bookingsByRequesterUserIdAndStartTime.items as GQLBooking[]
    const bookings: IBooking[] = await Promise.all(gqlBookings.map(async (gqlBooking) => {
      const item = await Booking.create({
        id: gqlBooking.id,
        status: gqlBooking.status,
        startTime: gqlBooking.startTime,
        endTime: gqlBooking.endTime,
        requesterUserId: gqlBooking.requesterUserId,
        hostUserId: gqlBooking.hostUserId,
        studioId: gqlBooking.studioId
      })
      return item
    }))
    return {
      items: bookings,
      nextToken: response.data.bookingsByRequesterUserIdAndStartTime.nextToken
    }
  }

  getHostedBookingByUserId = async (userId: string, pagination?: IPagination): Promise<IPaginatedList<IBooking>> => {
    const response = await GraphQLRequest({
      query: GET_BOOKINGS_BY_HOST,
      variables: {
        hostUserId: userId,
        limit: pagination?.limit ?? 10,
        nextToken: pagination?.nextToken,
        sortDirection: ModelSortDirection.DESC
      },
      publicQuery: true
    })
    // TODO: Make this query more cost effective by fetching users and studios in graphql
    const gqlBookings = response.data.bookingsByHostUserIdAndStartTime.items as GQLBooking[]
    const bookings: IBooking[] = await Promise.all(gqlBookings.map(async (gqlBooking) => {
      const item = await Booking.create({
        id: gqlBooking.id,
        status: gqlBooking.status,
        startTime: gqlBooking.startTime,
        endTime: gqlBooking.endTime,
        requesterUserId: gqlBooking.requesterUserId,
        hostUserId: gqlBooking.hostUserId,
        studioId: gqlBooking.studioId
      })
      return item
    }))
    return {
      items: bookings,
      nextToken: response.data.bookingsByHostUserIdAndStartTime.nextToken
    }
  }

  getUserStripeAccount = async (): Promise<UserStripeAccount> => {
    const response = await GraphQLRequest({
      query: GET_USER_STRIPE_ACCOUNT_QUERY
    })
    const account = response.data.getUserStripeAccount
    return {
      id: account?.id,
      onboarded: account?.onboarded ?? false,
      chargesEnabled: account?.chargesEnabled ?? false
    }
  }

  getStripeOnboardingLink = async (redirectRoute: string = '/settings/profile'): Promise<string> => {
    const input: GetStripeOnboardingLinkInput = {
      returnUrl: `${window.location.origin}/${redirectRoute}`,
      refreshUrl: `${window.location.origin}/${redirectRoute}`
    }
    console.log(input)
    const response = await GraphQLRequest({
      query: GET_USER_STRIPE_ONBOARDED_LINK_QUERY,
      variables: {
        input
      }
    })
    return response.data.getUserStripeOnboardingLink.url
  }

  getStripeLoginLink = async (): Promise<string> => {
    const response = await GraphQLRequest({
      query: GET_USER_STRIPE_LOGIN_LINK_QUERY
    })
    return response.data.getStripeLoginLink.url
  }
}
